import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiErrorFromResponse, authFetch } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { UploadJobOut } from '@/api/endpoints/youtube/uploads'

export type { UploadJobOut }

export interface TiktokAuthStatus {
  is_configured: boolean
  is_authenticated: boolean
  open_id: string
}

export interface TiktokUploadEntry {
  id: number
  video_id: string
  language: string
  status: string
  publish_id: string
  tiktok_video_id: string
  share_url: string
  title: string
  privacy: string
  error: string
  attempts: number
  source: string
  scheduled_for: string | null
  uploaded_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface TiktokUploadStatus {
  auth: TiktokAuthStatus
  uploads: TiktokUploadEntry[]
}

export interface TiktokUploadKickoffIn {
  video_id: string
  language: string
  channel?: string
  mode?: 'inbox' | 'direct'
  privacy?: string
  title?: string | null
}

export interface TiktokProfile {
  connected: boolean
  display_name: string
  follower_count: number
  video_count: number
  likes_count: number
  is_verified: boolean
}

/**
 * Disconnect the TikTok OAuth tokens for a channel. Hits the legacy
 * ``/auth/tiktok/disconnect`` endpoint (outside ``/api/v1``) which
 * lives on the render router because the OAuth round-trip itself
 * does. Success deletes the row in ``tiktok_tokens``; query invalidation
 * flips the chip back to "Desconectado" without a refresh.
 */
export function useDisconnectTiktok() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (channel: string) => {
      const res = await authFetch(`/auth/tiktok/disconnect?channel=${encodeURIComponent(channel)}`, {
        method: 'POST',
        credentials: 'same-origin',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao desconectar')
      }
      return (await res.json()) as { ok: boolean }
    },
    onSuccess: (_data, channel) => {
      void qc.invalidateQueries({ queryKey: queryKeys.uploads.tiktokStatus(channel) })
    },
  })
}

/**
 * Kick off a TikTok upload. Mirrors :func:`useStartUpload` (YouTube)
 * but talks to the new ``/api/v1/uploads/tiktok`` endpoint and accepts
 * the TikTok-specific ``mode`` (``inbox`` for sandbox / drafts vs
 * ``direct`` for live publishing once the app passes review). Returns
 * a job_key the UI can poll via the jobs tracker.
 */
export function useStartTiktokUpload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: TiktokUploadKickoffIn): Promise<UploadJobOut> => {
      const res = await authFetch('/api/v1/uploads/tiktok', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
        credentials: 'same-origin',
      })
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'Falha ao publicar no TikTok')
      }
      const body = (await res.json()) as { data?: UploadJobOut }
      if (!body.data) throw new Error('tiktok kickoff empty')
      return body.data
    },
    onSuccess: (_data, vars) => {
      const channel = vars.channel ?? ''
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
      if (channel) {
        void qc.invalidateQueries({ queryKey: queryKeys.uploads.tiktokStatus(channel) })
      }
    },
  })
}

/**
 * Fetch the connected TikTok profile counters via the Login Kit
 * ``user.info.*`` scopes. The endpoint hits TikTok on every call, so
 * the hook caches with a long staleTime — those counters change
 * minute-to-minute at most. Returns ``connected: false`` when the
 * channel hasn't OAuth-ed yet so the UI can render a clean placeholder.
 */
export function useTiktokProfile(channel: string) {
  return useQuery({
    queryKey: [...queryKeys.uploads.all, 'tiktok-profile', channel] as const,
    queryFn: async (): Promise<TiktokProfile> => {
      const res = await authFetch(
        `/api/v1/uploads/tiktok/profile?channel=${encodeURIComponent(channel)}`,
        { credentials: 'same-origin' },
      )
      if (!res.ok) throw await apiErrorFromResponse(res, 'Falha ao carregar o perfil do TikTok')
      const body = (await res.json()) as { data?: TiktokProfile }
      return (
        body.data ?? {
          connected: false,
          display_name: '',
          follower_count: 0,
          video_count: 0,
          likes_count: 0,
          is_verified: false,
        }
      )
    },
    enabled: Boolean(channel),
    staleTime: 5 * 60_000,
  })
}

/**
 * TikTok auth + uploads tracker for a channel. Mirrors
 * ``useUploadStatus`` but talks to ``/api/v1/uploads/tiktok/status``.
 * Goes through ``authFetch`` because the OpenAPI client regen hasn't
 * run for the new route yet — once it has, this can move to the
 * typed ``api.GET`` flavour.
 */
export function useTiktokUploadStatus(channel: string) {
  return useQuery({
    queryKey: queryKeys.uploads.tiktokStatus(channel),
    queryFn: async (): Promise<TiktokUploadStatus> => {
      const res = await authFetch(
        `/api/v1/uploads/tiktok/status?channel=${encodeURIComponent(channel)}`,
        { credentials: 'same-origin' },
      )
      if (!res.ok) throw await apiErrorFromResponse(res, 'Falha ao carregar os envios do TikTok')
      const body = (await res.json()) as { data?: TiktokUploadStatus }
      return (
        body.data ?? {
          auth: { is_configured: false, is_authenticated: false, open_id: '' },
          uploads: [],
        }
      )
    },
    enabled: Boolean(channel),
    refetchInterval: 15_000,
    staleTime: 5_000,
  })
}
